// src/hooks/useRoadSegmentMap.js
// --- UPDATE (GIS map — road segment health layer) -----------------------------
// Loads road segments for the GIS map (GET /roads/segments/map).
//   const seg = useRoadSegmentMap(roadIds, { enabled, all });
//   seg.data      { roads, segments, unsegmented, summary } | null
//   seg.loading / seg.error / seg.warnings / seg.at / seg.cached
//   seg.refresh()             reload now
//   seg.sliceRoads(ids)       slice mapped-but-unsliced roads (GET /roads/:id/segments auto-slices), then reload
//
//   • all: true  → the WHOLE network (every mapped road in the system) in one compact request;
//                  roadIds are ignored.
//   • all: false → only the given roads, one request per 300 ids (in parallel).
//   • Late responses from an older request are dropped.
//   • Live: refreshes (debounced) when a road on the map changes — segment health from Drive Mode,
//     ML segment evaluation, a submitted inspection, a re-drawn road. A live refresh asks the
//     server to skip its short cache (fresh=1) so the change shows straight away.
//   • Ids are compared as a sorted key, so re-renders with the same roads never refetch.
// --- END UPDATE ---------------------------------------------------------------
// --- UPDATE (roads v1.4): decodes TessaDEM ground (seg.ground) and the latest road ponding forecast (seg.ponding)
// from the appended compact columns; refreshes when a new forecast is pushed (`road_ponding_forecast`).
// --- END UPDATE ---------------------------------------------------------------
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from '../components/roads/page/shared';
import useFlowSenseLive from './useFlowSenseLive';
import webSocketService from '../services/websocketService'; // --- UPDATE (roads v1.4)

const CHUNK = 300;
const SLICE_CONCURRENCY = 3;
const EMPTY = { roads: [], segments: [], unsegmented: [], summary: { roads: 0, segments: 0 } };
const LIVE_TYPES = /segment|drive|evaluat|inspection|checklist|geometry|polyline|updated|created|deleted|work_order/i;

/** format=compact rows → the segment objects the map layer uses. */
export function decodeSegments(segments) {
    if (Array.isArray(segments)) return segments;
    if (!segments || !Array.isArray(segments.cols) || !Array.isArray(segments.rows)) return [];
    const i = Object.fromEntries(segments.cols.map((c, k) => [c, k]));
    const get = (row, c) => (i[c] === undefined ? null : row[i[c]] ?? null);
    return segments.rows.map((row) => {
        const sLat = get(row, 'start_lat'); const sLng = get(row, 'start_lng');
        const eLat = get(row, 'end_lat'); const eLng = get(row, 'end_lng');
        const hasDrive = i.drive_findings !== undefined;
        // --- UPDATE (roads v1.4)
        const hasGround = i.ground_start_m !== undefined;
        const pondBand = i.ponding_band !== undefined ? get(row, 'ponding_band') : null;
        // --- END UPDATE
        return {
            id: String(get(row, 'id')),
            road_id: String(get(row, 'road_id')),
            index: get(row, 'index'),
            station_start_m: get(row, 'station_start_m'),
            station_end_m: get(row, 'station_end_m'),
            length_m: get(row, 'length_m'),
            start: sLat !== null && sLng !== null ? { lat: sLat, lng: sLng } : null,
            end: eLat !== null && eLng !== null ? { lat: eLat, lng: eLng } : null,
            pci: get(row, 'pci'),
            potholes: get(row, 'potholes'),
            cracking: get(row, 'cracking'),
            rutting_mm: get(row, 'rutting_mm'),
            rul_years: get(row, 'rul_years'),
            crown_m: get(row, 'crown_m'),
            invert_m: get(row, 'invert_m'),
            last_inspected_at: get(row, 'last_inspected_at'),
            updated_at: get(row, 'updated_at'),
            drive: hasDrive ? {
                findings: get(row, 'drive_findings'), confirmed: get(row, 'drive_confirmed'), potholes: get(row, 'drive_potholes'),
                cracking: get(row, 'drive_cracking'), at: get(row, 'drive_at'),
            } : null,
            // --- UPDATE (roads v1.4)
            ground: hasGround ? {
                start_m: get(row, 'ground_start_m'), mid_m: get(row, 'ground_mid_m'), end_m: get(row, 'ground_end_m'),
                low_m: get(row, 'ground_low_m'), slope_pct: get(row, 'ground_slope_pct'),
            } : null,
            ponding: pondBand ? {
                band: pondBand, depth_m: get(row, 'ponding_depth_m'), starts_hours: get(row, 'ponding_starts_hours'),
                clears_hours: get(row, 'ponding_clears_hours'), kind: get(row, 'ponding_kind'),
                drain_asset_id: get(row, 'ponding_drain_asset_id'), at: get(row, 'ponding_at'),
            } : null,
            // --- END UPDATE
        };
    });
}

export default function useRoadSegmentMap(roadIds, { enabled = true, all = false } = {}) {
    const key = useMemo(() => (all ? '*' : [...new Set((roadIds || []).filter((x) => x !== null && x !== undefined).map(String))].sort().join(',')), [roadIds, all]);
    const [data, setData] = useState(null);
    const [state, setState] = useState({ loading: false, error: null, warnings: [], at: null, cached: false });
    const [slicing, setSlicing] = useState(false);
    const seqRef = useRef(0);
    const mountedRef = useRef(true);
    useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

    const load = useCallback(async (silent = false, fresh = false) => {
        if (!enabled) return;
        const seq = ++seqRef.current;
        const ids = key === '*' ? null : (key ? key.split(',') : []);
        if (ids && !ids.length) { setData(EMPTY); setState({ loading: false, error: null, warnings: [], at: new Date(), cached: false }); return; }
        if (!silent) setState((s) => ({ ...s, loading: true, error: null }));
        try {
            let results;
            if (!ids) {
                results = [await apiFetch(`/roads/segments/map?all=1&format=compact${fresh ? '&fresh=1' : ''}`)];
            } else {
                const chunks = [];
                for (let i = 0; i < ids.length; i += CHUNK) chunks.push(ids.slice(i, i + CHUNK));
                results = await Promise.all(chunks.map((c) => apiFetch(`/roads/segments/map?ids=${encodeURIComponent(c.join(','))}`)));
            }
            if (!mountedRef.current || seq !== seqRef.current) return;
            const merged = { roads: [], segments: [], unsegmented: [], summary: null };
            const warnings = new Set();
            results.forEach((r) => {
                const d = r?.data || {};
                merged.roads.push(...(d.roads || []));
                merged.segments.push(...decodeSegments(d.segments));
                merged.unsegmented.push(...(d.unsegmented || []));
                if (results.length === 1) merged.summary = d.summary || null;
                (r?.warnings || []).forEach((w) => warnings.add(w));
            });
            setData(merged);
            setState({ loading: false, error: null, warnings: [...warnings], at: new Date(), cached: results.length === 1 && !!results[0]?.cached });
        } catch (err) {
            if (!mountedRef.current || seq !== seqRef.current) return;
            setState((s) => ({ ...s, loading: false, error: err?.message || 'Road segments could not be loaded.' }));
        }
    }, [key, enabled]);

    useEffect(() => { load(false); }, [load]);

    // live: any change to a road on the map (whole network: any road)
    const loadedIds = useMemo(() => new Set((data?.roads || []).map((r) => String(r.id))), [data]);
    const idSet = useMemo(() => (key === '*' ? null : new Set(key ? key.split(',') : [])), [key]);
    const liveFilter = useCallback((name, msg) => {
        const p = msg?.payload || msg?.data || msg || {};
        const rid = p.road_id ?? p.roadId ?? p.asset_id ?? p.id ?? null;
        if (rid === null || rid === undefined) return false;
        const known = idSet ? idSet.has(String(rid)) : (loadedIds.has(String(rid)) || /geometry|polyline|created|segments_generated/i.test(String(p.type || '')));
        if (!known) return false;
        return !p.type || LIVE_TYPES.test(String(p.type));
    }, [idSet, loadedIds]);
    useFlowSenseLive(['roads'], () => load(true, true), { debounceMs: key === '*' ? 5000 : 2000, filter: liveFilter, enabled: enabled && (key === '*' || (idSet?.size || 0) > 0) });
    // --- UPDATE (roads v1.4): a new road ponding forecast was written onto the segments → reload (debounced)
    const loadRef = useRef(load); loadRef.current = load;
    useEffect(() => {
        if (!enabled) return undefined;
        let t = null;
        const onForecast = () => { clearTimeout(t); t = setTimeout(() => loadRef.current(true, true), 4000); };
        const off = webSocketService.subscribe?.('road_ponding_forecast', onForecast);
        return () => { clearTimeout(t); if (typeof off === 'function') off(); else webSocketService.unsubscribe?.('road_ponding_forecast', onForecast); };
    }, [enabled]);
    // --- END UPDATE

    const sliceRoads = useCallback(async (ids) => {
        const list = [...new Set((ids || []).map(String))];
        if (!list.length) return { sliced: 0, failed: 0 };
        setSlicing(true);
        let sliced = 0; let failed = 0;
        try {
            for (let i = 0; i < list.length; i += SLICE_CONCURRENCY) {
                // eslint-disable-next-line no-await-in-loop
                const out = await Promise.allSettled(list.slice(i, i + SLICE_CONCURRENCY).map((id) => apiFetch(`/roads/${encodeURIComponent(id)}/segments`)));
                out.forEach((o) => { if (o.status === 'fulfilled' && (o.value?.data || []).length) sliced += 1; else failed += 1; });
            }
        } finally {
            if (mountedRef.current) setSlicing(false);
        }
        await load(true, true);
        return { sliced, failed };
    }, [load]);

    const refresh = useCallback(() => load(true, true), [load]);
    return { data, ...state, slicing, refresh, sliceRoads };
}
